import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, User, Phone,
  CheckCircle, Loader2, Sparkles, Moon, Heart
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { PhoneAuth } from '../components/PhoneAuth';
import { ZodiacMandala, TwinkleField } from '../components/shubh/ZodiacMandala';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const FieldInput = ({ icon: Icon, right, ...props }) => (
  <div className="relative group">
    <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9A948C] group-focus-within:text-[#C84B32] transition-colors" />
    <input
      {...props}
      className="w-full py-3.5 pl-12 pr-12 rounded-2xl bg-white border border-[#282622]/15 text-[#282622] text-[15px] placeholder:text-[#9A948C] focus:outline-none focus:border-[#C84B32] focus:ring-4 focus:ring-[#C84B32]/10 transition-[border-color,box-shadow]"
    />
    {right}
  </div>
);

const PrimaryButton = ({ loading, children, ...props }) => (
  <motion.button
    {...props}
    disabled={loading || props.disabled}
    whileHover={{ y: -2 }}
    whileTap={{ scale: 0.98 }}
    className="shubh-shimmer w-full h-14 rounded-full font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-70"
    style={{ background: 'linear-gradient(135deg, #C84B32 0%, #E85D43 60%, #E8913A 100%)', boxShadow: '0 10px 28px rgba(200,75,50,0.35)' }}
  >
    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : children}
  </motion.button>
);

const ErrorNote = ({ error }) =>
  error ? (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm"
      data-testid="login-error"
    >
      {error}
    </motion.div>
  ) : null;

const ForgotPasswordView = ({ isHindi, onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [demoLink, setDemoLink] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      if (res.data?.demo_mode && res.data?.reset_link) setDemoLink(res.data.reset_link);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-2">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-50 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-[#287A60]" />
        </div>
        <h2 className="font-['Fraunces'] text-2xl text-[#282622] mb-2">{demoLink ? 'Demo Mode' : 'Check Your Email'}</h2>
        <p className="text-[#6B6560] text-sm mb-4">
          {demoLink ? 'Email sending is not configured. Use the link below to reset your password:' : <>We've sent a reset link to <span className="text-[#C84B32] font-semibold">{email}</span></>}
        </p>
        {demoLink && (
          <a href={demoLink} className="block mb-5 p-3 rounded-xl bg-[#FCE9D9]/60 border border-[#F5B942]/40 text-[#C84B32] text-sm font-medium hover:underline" data-testid="demo-reset-link">
            Click here to reset password
          </a>
        )}
        <PrimaryButton type="button" onClick={onBack} data-testid="back-to-login-btn">
          <ArrowLeft className="w-5 h-5" /> {isHindi ? 'लॉगिन पर वापस' : 'Back to Login'}
        </PrimaryButton>
      </div>
    );
  }

  return (
    <>
      <button type="button" onClick={onBack} className="flex items-center text-sm text-[#6B6560] hover:text-[#C84B32] transition-colors mb-5" data-testid="forgot-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> {isHindi ? 'वापस' : 'Back to login'}
      </button>
      <h2 className="font-['Fraunces'] text-3xl text-[#282622] mb-1">{isHindi ? 'पासवर्ड रीसेट' : 'Reset Password'}</h2>
      <p className="text-[#6B6560] text-sm mb-6">{isHindi ? 'रीसेट लिंक के लिए ईमेल डालें' : 'Enter your email to receive a reset link'}</p>
      <ErrorNote error={error} />
      <form onSubmit={submit} className="space-y-5">
        <FieldInput icon={Mail} type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="reset-email-input" />
        <PrimaryButton type="submit" loading={loading} data-testid="send-reset-link-btn">
          {isHindi ? 'रीसेट लिंक भेजें' : 'Send Reset Link'} <ArrowRight className="w-5 h-5" />
        </PrimaryButton>
      </form>
    </>
  );
};

const TabSwitch = ({ isSignUp, setIsSignUp, isHindi }) => (
  <div className="relative flex p-1 rounded-full bg-[#FCE9D9]/70 mb-7" role="tablist">
    <motion.span
      className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-white shadow-sm"
      animate={{ x: isSignUp ? '100%' : '0%' }}
      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
    />
    {[false, true].map((signUp) => (
      <button
        key={String(signUp)}
        type="button"
        role="tab"
        aria-selected={isSignUp === signUp}
        onClick={() => setIsSignUp(signUp)}
        className={`relative z-10 flex-1 py-2.5 text-sm font-semibold transition-colors ${isSignUp === signUp ? 'text-[#C84B32]' : 'text-[#6B6560]'}`}
        data-testid={signUp ? 'login-tab-signup' : 'login-tab-signin'}
      >
        {signUp ? (isHindi ? 'रजिस्टर' : 'Sign Up') : (isHindi ? 'लॉगिन' : 'Sign In')}
      </button>
    ))}
  </div>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const SocialButtons = ({ isHindi, onGoogle, onPhone }) => (
  <>
    <div className="flex items-center gap-4 my-6">
      <div className="flex-1 h-px bg-[#282622]/10" />
      <span className="text-xs uppercase tracking-[0.2em] text-[#9A948C]">{isHindi ? 'या' : 'or'}</span>
      <div className="flex-1 h-px bg-[#282622]/10" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <button type="button" onClick={onGoogle} className="h-12 rounded-full bg-white border border-[#282622]/15 text-[#282622] text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#F5B942] hover:shadow-md transition-[border-color,box-shadow]" data-testid="google-login-btn">
        <GoogleIcon /> Google
      </button>
      <button type="button" onClick={onPhone} className="h-12 rounded-full bg-white border border-[#282622]/15 text-[#282622] text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#F5B942] hover:shadow-md transition-[border-color,box-shadow]" data-testid="phone-login-btn">
        <Phone className="w-4 h-4 text-[#287A60]" /> {isHindi ? 'फोन' : 'Phone'}
      </button>
    </div>
  </>
);

const EmailAuthForm = ({ isHindi, onForgot }) => {
  const navigate = useNavigate();
  const { setUser, loginWithGoogle } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPhone, setShowPhone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (isSignUp && form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    const payload = isSignUp ? form : { email: form.email.trim().toLowerCase(), password: form.password };
    let token;
    try {
      const res = await axios.post(`${API}/auth/${isSignUp ? 'register' : 'login'}`, payload, { withCredentials: true });
      token = res.data?.access_token;
      if (token) localStorage.setItem('auth_token', token);
    } catch (err) {
      setLoading(false);
      if (!err.response) {
        setError(isHindi ? 'सर्वर से कनेक्ट नहीं हो पाया। इंटरनेट जांचें और पुनः प्रयास करें।' : "Can't reach the server. Check your internet and try again.");
      } else if (err.response.status === 401) {
        setError(isHindi
          ? 'ईमेल या पासवर्ड गलत है। अगर आपने फोन नंबर से खाता बनाया था, तो "फोन" से लॉगिन करें।'
          : 'Incorrect email or password. If you signed up with your phone number, use "Phone" login below.');
      } else {
        setError(err.response.data?.detail || (isSignUp ? 'Registration failed' : 'Login failed. Please try again.'));
      }
      return;
    }
    try {
      const me = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(me.data);
      navigate('/dashboard');
    } catch (err) {
      setError(isHindi ? 'लॉगिन हुआ, पर खाता लोड नहीं हुआ। कृपया पुनः प्रयास करें।' : 'Signed in, but your account could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showPhone) {
    return <PhoneAuth onBack={() => setShowPhone(false)} onSuccess={() => navigate('/dashboard')} />;
  }

  return (
    <>
      <TabSwitch isSignUp={isSignUp} setIsSignUp={(v) => { setIsSignUp(v); setError(''); }} isHindi={isHindi} />
      <h2 className="font-['Fraunces'] text-3xl text-[#282622] mb-1" data-testid="login-heading">
        {isSignUp ? (isHindi ? 'अपनी यात्रा शुरू करें' : 'Begin your journey') : (isHindi ? 'फिर से स्वागत है' : 'Welcome back')}
      </h2>
      <p className="text-[#6B6560] text-sm mb-6">
        {isSignUp ? (isHindi ? 'आपके सितारे आपका इंतज़ार कर रहे हैं' : 'Your stars have been waiting for you') : (isHindi ? 'आज आपके ग्रह क्या कहते हैं?' : "Let's see what your planets say today")}
      </p>
      <ErrorNote error={error} />
      <form onSubmit={submit} className="space-y-4">
        <AnimatePresence initial={false}>
          {isSignUp && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
              <FieldInput icon={User} type="text" placeholder={isHindi ? 'पूरा नाम' : 'Full name'} value={form.name} onChange={update('name')} required data-testid="name-input" />
            </motion.div>
          )}
        </AnimatePresence>
        <FieldInput icon={Mail} type="email" placeholder="you@example.com" value={form.email} onChange={update('email')} required data-testid="email-input" />
        <FieldInput
          icon={Lock}
          type={showPassword ? 'text' : 'password'}
          placeholder={isSignUp ? 'Create a password (min 6 chars)' : 'Your password'}
          value={form.password}
          onChange={update('password')}
          required
          minLength={6}
          data-testid="password-input"
          right={
            <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A948C] hover:text-[#C84B32]" aria-label="Toggle password visibility" data-testid="toggle-password-visibility">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          }
        />
        {!isSignUp && (
          <div className="text-right -mt-1">
            <button type="button" onClick={onForgot} className="text-sm font-semibold text-[#C84B32] hover:text-[#A63D28]" data-testid="forgot-password-link">
              {isHindi ? 'पासवर्ड भूल गए?' : 'Forgot password?'}
            </button>
          </div>
        )}
        <PrimaryButton type="submit" loading={loading} data-testid="submit-btn">
          {isSignUp ? (isHindi ? 'खाता बनाएं' : 'Create Account') : (isHindi ? 'लॉगिन करें' : 'Sign In')}
          <ArrowRight className="w-5 h-5" />
        </PrimaryButton>
      </form>
      <SocialButtons isHindi={isHindi} onGoogle={loginWithGoogle} onPhone={() => setShowPhone(true)} />
    </>
  );
};

const HeroCopy = ({ isHindi }) => (
  <div className="text-center lg:text-left">
    <motion.p {...fadeUp(0.3)} className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C84B32] mb-3">
      {isHindi ? 'वैदिक ज्योतिष • भारत' : 'Vedic Jyotish • Made in India'}
    </motion.p>
    <motion.h1 {...fadeUp(0.4)} className="font-['Fraunces'] text-4xl sm:text-5xl lg:text-6xl text-[#282622] leading-[1.05] mb-4">
      {isHindi ? <>आपके सितारे,<br /><span className="italic text-[#C84B32]">आपकी कहानी</span></> : <>Your stars,<br /><span className="italic text-[#C84B32]">your story</span></>}
    </motion.h1>
    <motion.p {...fadeUp(0.5)} className="text-[#6B6560] text-base max-w-md mx-auto lg:mx-0">
      {isHindi ? 'कुंडली, राशिफल, कुंडली मिलान और वैदिक गुरु — सब एक जगह।' : 'Kundli, daily Rashifal, Kundli Milan and a voice-first Vedic Guru — all in one auspicious place.'}
    </motion.p>
    <motion.div {...fadeUp(0.6)} className="hidden lg:flex gap-3 mt-8">
      {[
        { icon: Moon, label: isHindi ? '27 नक्षत्र' : '27 Nakshatras' },
        { icon: Sparkles, label: isHindi ? 'AI गुरु' : 'AI Guru' },
        { icon: Heart, label: isHindi ? 'गुण मिलान' : 'Guna Milan' },
      ].map(({ icon: Icon, label }) => (
        <span key={label} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-[#F5B942]/40 text-sm font-medium text-[#4A4640] backdrop-blur">
          <Icon className="w-4 h-4 text-[#C84B32]" /> {label}
        </span>
      ))}
    </motion.div>
  </div>
);

const ShubhLoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const isHindi = language === 'hi';
  const [view, setView] = useState('auth');

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FFF9EF 0%, #FCE9D9 55%, #F9DCCB 100%)' }} data-testid="shubh-login-page">
      <TwinkleField />
      <motion.div
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(246,199,211,0.55), transparent 65%)' }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />

      <header className="relative z-20 flex items-center justify-between px-5 sm:px-10 pt-6">
        <Link to="/" className="flex items-center gap-2" data-testid="login-brand-link">
          <span className="w-9 h-9 rounded-full flex items-center justify-center text-white font-['Fraunces'] text-lg" style={{ background: 'linear-gradient(135deg, #F5B942, #C84B32)' }}>ॐ</span>
          <span className="font-['Fraunces'] text-xl text-[#282622]">Nakshatra</span>
        </Link>
        <button onClick={toggleLanguage} className="px-4 py-2 rounded-full bg-white/80 backdrop-blur border border-[#282622]/10 text-sm font-semibold text-[#282622] hover:border-[#F5B942] transition-colors" data-testid="login-language-toggle">
          {isHindi ? 'English' : 'हिंदी'}
        </button>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-10 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="flex flex-col items-center lg:items-start gap-8">
          <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="w-64 sm:w-80 lg:w-[420px]">
            <ZodiacMandala className="w-full" />
          </motion.div>
          <HeroCopy isHindi={isHindi} />
        </div>

        <motion.div {...fadeUp(0.35)} className="w-full max-w-md mx-auto lg:ml-auto">
          <div className="rounded-[28px] p-7 sm:p-9 bg-white/85 backdrop-blur-xl border border-white shadow-[0_24px_70px_rgba(82,58,26,0.14)]" data-testid="login-auth-card">
            <AnimatePresence mode="wait">
              <motion.div key={view} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                {view === 'forgot'
                  ? <ForgotPasswordView isHindi={isHindi} onBack={() => setView('auth')} />
                  : <EmailAuthForm isHindi={isHindi} onForgot={() => setView('forgot')} />}
              </motion.div>
            </AnimatePresence>
          </div>
          <p className="text-center text-xs text-[#8A8279] mt-5">
            {isHindi ? 'जारी रखकर, आप हमारी ' : 'By continuing, you agree to our '}
            <Link to="/privacy-policy" className="text-[#C84B32] font-medium hover:underline" data-testid="login-privacy-link">
              {isHindi ? 'गोपनीयता नीति' : 'Privacy Policy'}
            </Link>
            {isHindi ? ' से सहमत हैं' : '.'}
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default ShubhLoginPage;
